import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button } from "antd";
import { useHistory, useRouteMatch } from "react-router-dom";
import { GET_STAFF_MANAGEMENT_WORKERS } from "graphql/query/staff-management-gql";
import { Tables } from "components/layout";
import { Localize } from "components/service";
import StaffManagementConst from "components/staff-management/staff-management-const";
import StaffWorkersTableFilter from "components/staff-management/filters/staff-workers-table-filter";
import StaffManagementHooks from "components/staff-management/hooks";
import useStaffWorkerStatusSessions from "components/staff-management/hooks/use-staff-worker-status-sessions";
import {
    getStaffWorkerSessionSummaries,
    getStaffWorkerStatusFilter,
    getStaffWorkersQueryState,
    STAFF_WORKERS_PAGE_SIZE,
} from "components/staff-management/helpers/staff-workers-api-helper";
import StaffWorkersTableHelper from "components/staff-management/helpers/staff-workers-table-helper";


const WORKERS_ORDER_BY = [
    { column: "NAME", order: "ASC" },
    { column: "SURNAME", order: "ASC" },
    { column: "ID", order: "ASC" },
];

const EMPTY_WORKERS_MODEL = {
    data: [],
    paginatorInfo: { total: 0, currentPage: 1, perPage: STAFF_WORKERS_PAGE_SIZE },
};

const StaffManagementWorkersSubPage = ({ event }) => {
    const history = useHistory();
    const routeUrl = `${ StaffManagementConst.basePath }/${ event.id }/workers`;
    const match = useRouteMatch(`${ routeUrl }/page/:pageNum`);
    const parsedPage = match ? Number(match.params.pageNum) : 1;
    const validRequestedPage = Number.isInteger(parsedPage) && parsedPage > 0;
    const canonicalPagePath = !match || (validRequestedPage && parsedPage > 1);
    const requestedPage = validRequestedPage ? parsedPage : 1;
    const [ searchText, setSearchText ] = useState("");
    const [ roleID, setRoleID ] = useState();
    const [ subcontractorID, setSubcontractorID ] = useState();
    const [ status, setStatus ] = useState();
    const [ visibleWorkers, setVisibleWorkers ] = useState([]);
    const statusSessions = useStaffWorkerStatusSessions({ eeID: event.id, enabled: !!status });
    const statusFilter = useMemo(() => getStaffWorkerStatusFilter({
        status,
        sessions: statusSessions.sessions,
    }), [ status, statusSessions.sessions ]);
    const queryState = useMemo(() => getStaffWorkersQueryState({
        eventRoles: event.relWorkerRoles,
        selectedRoleID: roleID,
        subcontractorID,
        statusWhere: statusFilter.where,
    }), [ event.relWorkerRoles, roleID, subcontractorID, statusFilter.where ]);
    const { sessionSummaries: pageSessionSummaries } = StaffManagementHooks.useStaffWorkerSessions({
        eeID: event.id,
        workers: status ? [] : visibleWorkers,
    });
    const sessionSummaries = useMemo(() => status ?
        getStaffWorkerSessionSummaries(visibleWorkers, statusSessions.sessions) :
        pageSessionSummaries,
    [ status, visibleWorkers, statusSessions.sessions, pageSessionSummaries ]);
    const holdStatusResults = !!status && (!statusSessions.ready || statusFilter.noMatches);

    const resetPage = useCallback(() => {
        if (requestedPage !== 1)
        {
            history.push(routeUrl);
        }
    }, [ history, requestedPage, routeUrl ]);

    useEffect(() => {
        if (!canonicalPagePath)
        {
            history.replace(routeUrl);
        }
    }, [ canonicalPagePath, history, routeUrl ]);

    useEffect(() => {
        if (roleID && !queryState.eligibleRoleIDs.includes(String(roleID)))
        {
            setRoleID(undefined);
            resetPage();
        }
    }, [ queryState.eligibleRoleIDs, resetPage, roleID ]);

    return (
        <div className="staff-management-view-shell--workers">
            <Tables.Main
                model="users"
                query={ GET_STAFF_MANAGEMENT_WORKERS }
                routeUrl={ routeUrl }
                currentPage={ requestedPage }
                externalModel={ holdStatusResults ? EMPTY_WORKERS_MODEL : undefined }
                externalLoading={ !!status && statusSessions.loading }
                tableVisible={ !status || !statusSessions.error }
                searchText={ searchText }
                objFilter={ queryState.where }
                objOrderBy={ WORKERS_ORDER_BY }
                currentObj={ { sessionSummaries, eeID: event.id } }
                onData={ setVisibleWorkers }
                varSet={ {
                    perPage: STAFF_WORKERS_PAGE_SIZE,
                    eeID: [ String(event.id) ],
                } }
                tableHelper={ StaffWorkersTableHelper }
                extraClass="staff-workers-table"
                scroll={ { x: 1368 } }>
                <StaffWorkersTableFilter
                    eventRoles={ event.relWorkerRoles || [] }
                    eventSubcontractors={ event.relWorkerSubcontractors || [] }
                    searchText={ searchText }
                    setSearchText={ setSearchText }
                    roleID={ roleID }
                    setRoleID={ setRoleID }
                    subcontractorID={ subcontractorID }
                    setSubcontractorID={ setSubcontractorID }
                    status={ status }
                    setStatus={ setStatus }
                    resetPage={ resetPage } />
                { status && statusSessions.error &&
                    <Alert
                        type="error"
                        showIcon
                        message={ <Localize>STAFF_MANAGEMENT.Error_LoadWorkerStatuses</Localize> }
                        action={
                            <Button onClick={ statusSessions.retry }>
                                <Localize>STAFF_MANAGEMENT.Action_Retry</Localize>
                            </Button>
                        } />
                }
            </Tables.Main>
        </div>
    );
};

export default StaffManagementWorkersSubPage;
